import React, { useState } from "react";
import PlayCircleFilledRoundedIcon from "@mui/icons-material/PlayCircleFilledRounded";
import "../styles/MaterialsRenderer.scss";
import pdfImg from "../assets/images/pdfIcon.svg";
import audioIcon from "../assets/png/audioIcon.png";
import imgIcon from "../assets/png/imgIcon.png";
import powerpointIcon from "../assets/png/powerpointIcon.png";
import musicIcon from "../assets/png/musicIcon.png";

const MaterialsRenderer = ({ content }) => {
  const [imageError, setImageError] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [pptViewer, setPptViewer] = useState("office");

  if (!content || !Array.isArray(content) || content.length === 0) {
    return <div className="materials-empty">No materials available.</div>;
  }

  const renderMaterialIcon = (item) => {
    const type = (item?.type || "").toLowerCase();
    if (type === "pdf") {
      return (
        <div className="material-icon">
          <img src={pdfImg} alt="PDF" className="material-icon-img" />
        </div>
      );
    }
    if (type === "video") {
      return (
        <div className="material-icon">
          <PlayCircleFilledRoundedIcon className="material-icon-video" />
        </div>
      );
    }
    if (type === "audio") {
      return (
        <div className="material-icon">
          <img src={audioIcon} alt="Audio" className="material-icon-img" />
        </div>
      );
    }
    if (type === "image") {
      return (
        <div className="material-icon">
          <img src={imgIcon} alt="Image" className="material-icon-img" />
        </div>
      );
    }
    if (type === "ppt" || type === "powerpoint") {
      return (
        <div className="material-icon">
          <img
            src={powerpointIcon}
            alt="PowerPoint"
            className="material-icon-img"
          />
        </div>
      );
    }
    return (
      <div className="material-icon">
        <img src={imgIcon} alt="File" className="material-icon-img" />
      </div>
    );
  };

  const renderMaterialContent = (item) => {
    const type = (item?.type || "").toLowerCase();
    const link = item?.content || "";
    const name = item?.name || "";

    const isVideo = type === "video";
    const isPdf = type === "pdf";
    const isAudio = type === "audio";
    const isImage = type === "image";
    const isPpt = type === "ppt" || type === "powerpoint";

    const isYouTubeLink =
      typeof link === "string" &&
      (link.includes("youtube.com") || link.includes("youtu.be"));

    let youtubeEmbedUrl = null;
    if (isYouTubeLink) {
      try {
        if (link.includes("youtube.com/watch")) {
          const url = new URL(link);
          const v = url.searchParams.get("v");
          if (v) {
            youtubeEmbedUrl = `https://www.youtube.com/embed/${v}`;
          }
        } else if (link.includes("youtu.be/")) {
          const id = link.split("youtu.be/")[1]?.split(/[?&#]/)[0];
          if (id) {
            youtubeEmbedUrl = `https://www.youtube.com/embed/${id}`;
          }
        }
      } catch (e) {
        youtubeEmbedUrl = null;
      }
    }

    return (
      <div className="material-content">
        {isVideo && link && youtubeEmbedUrl && (
          <iframe
            title={name || "Video"}
            src={youtubeEmbedUrl}
            className="material-video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        )}

        {isVideo && link && !youtubeEmbedUrl && (
          <video controls className="material-video">
            <source src={link} />
            Your browser does not support the video tag.
          </video>
        )}

        {isPdf && link && link.toLowerCase().includes(".pdf") && (
          <iframe title={name || "PDF"} src={link} className="material-pdf" />
        )}

        {isPdf && link && !link.toLowerCase().includes(".pdf") && (
          <div className="material-error">
            <p>This material is an invalid link.</p>
          </div>
        )}

        {isAudio && link && !audioError && (
          <div className="material-audio-container">
            {/* <img
              src={musicIcon}
              alt="Audio Icon"
              className="material-audio-icon"
            /> */}
            <audio
              controls
              className="material-audio"
              onError={() => setAudioError(true)}
            >
              <source src={link} />
              Your browser does not support the audio element.
            </audio>
          </div>
        )}

        {isAudio && audioError && (
          <div className="material-error">
            <p>Unable to play audio. The file might not be accessible.</p>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="material-download-btn"
            >
              Download Audio File
            </a>
          </div>
        )}

        {isImage && link && !imageError && (
          <img
            src={link}
            alt={name || "Image"}
            className="material-image"
            onError={() => setImageError(true)}
          />
        )}

        {isImage && imageError && (
          <div className="material-error">
            <p>Unable to load image. The file might not be accessible.</p>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="material-download-btn"
            >
              Open Image in New Tab
            </a>
          </div>
        )}

        {isPpt && link && (
          <div className="material-ppt-container">
            {pptViewer === "office" && (
              <iframe
                title={name || "Presentation"}
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                  link,
                )}`}
                className="material-ppt"
                onError={() => setPptViewer("google")}
              />
            )}

            {pptViewer === "google" && (
              <iframe
                title={name || "Presentation"}
                src={`https://docs.google.com/gview?url=${encodeURIComponent(
                  link,
                )}&embedded=true`}
                className="material-ppt"
                onError={() => setPptViewer("failed")}
              />
            )}

            {pptViewer === "failed" && (
              <div className="material-error">
                <p>
                  <strong>{name || "Presentation"}</strong>
                </p>
                <p>Preview not available for this presentation.</p>
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="material-download-btn"
                >
                  Download Presentation
                </a>
              </div>
            )}
          </div>
        )}

        {!link && (
          <div className="material-error">
            <p>No content URL configured for this material.</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="materials-renderer  custom-scrollbar">
      <div className="materials-grid">
        {content.map((item, index) => (
          <div key={item.id || index} className="material-item">
            <div className="material-header">
              {renderMaterialIcon(item)}
              <h4 className="material-title">{item.name}</h4>
            </div>
            {renderMaterialContent(item)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MaterialsRenderer;
